import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { COLORS, tooltipStyle } from '../../utils/colors';
import { useColorScheme } from '../../hooks/useColorScheme';

/**
 * Horizontal bar chart for Commitment or Rejection Index.
 *
 * Props:
 *   data.overall  — { "Option A": { "Committed": 37.4 }, "Option B": { "Committed": 3.1 } }
 *   data.filtered — same shape, or null
 *   type          — 'commitment' or 'rejection'
 */
export default function CohortChart({ data, type }) {
  const { colors: schemeColors } = useColorScheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data?.overall) return;

    if (chartRef.current) chartRef.current.destroy();

    const color = type === 'rejection' ? schemeColors.coral : schemeColors.teal;

    const optionNames = Object.keys(data.overall);
    const labels = optionNames.map(n => truncateLabel(n, 45));
    const fullLabels = optionNames;

    // Get the percentage value — try known key names, fall back to first value
    const overallValues = optionNames.map(opt => {
      const cohorts = data.overall[opt];
      if (typeof cohorts === 'number') return cohorts;
      // Try various possible key names
      const tryKeys = type === 'rejection'
        ? ['Rejected', 'rejected', 'Rejection', 'rejection', 'RI', 'ri']
        : ['Committed', 'committed', 'Commitment', 'commitment', 'CI', 'ci'];
      for (const k of tryKeys) {
        if (cohorts[k] != null) return cohorts[k];
      }
      // Fall back to the first (or max) value
      const vals = Object.values(cohorts).filter(v => typeof v === 'number');
      return vals.length > 0 ? Math.max(...vals) : 0;
    });

    const datasets = [{
      label: type === 'rejection' ? 'Rejection Index' : 'Commitment Index',
      data: overallValues,
      backgroundColor: color,
      borderWidth: 0,
      borderRadius: 6,
      barPercentage: 0.6,
      categoryPercentage: 0.8
    }];

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        _fullLabels: fullLabels,
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            grid: { color: '#f0f1f3' },
            ticks: {
              font: { size: 11 },
              color: COLORS.textMuted,
              callback: (v) => v + '%'
            }
          },
          y: {
            grid: { display: false },
            ticks: {
              font: { size: 12, weight: 500 },
              color: COLORS.textSecondary,
              padding: 8
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              title: (items) => fullLabels[items[0].dataIndex],
              label: (item) => ` ${item.dataset.label}: ${item.raw.toFixed(1)}%`
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'right',
            color: COLORS.textSecondary,
            font: { size: 11, weight: 600 },
            formatter: (v) => v.toFixed(1) + '%'
          }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data, type, schemeColors]);

  return <canvas ref={canvasRef} />;
}

function truncateLabel(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen - 3) + '...' : text;
}
